/**
 * URL search-param contract for the voter-file browser (`/your-voters`).
 *
 * Before this existed, every filter / sort / page choice lived only in
 * component `useState`, so it was lost on refresh, on back-navigation, and
 * couldn't be shared as a link. Putting it in the URL also gives the voter
 * detail page something to return to — closing a voter restores the exact
 * list the user opened it from.
 *
 * The filter set is stored as a JSON blob under a single `f` key rather than
 * exploded into one param per filter. The filter values are heterogeneous
 * (text/enum/range/boolean shapes, see ColumnFilterValue) and a flat encoding
 * would need a per-type codec on both sides; the API-param serialization in
 * `columnFiltersToApiParams` is already the canonical flattening and is
 * deliberately lossy (it drops scope/operator metadata), so it can't be
 * round-tripped back into filter state.
 */

import type { ColumnFiltersState } from "@tanstack/react-table";

export type VoterFileSearch = {
	/** JSON-encoded ColumnFiltersState. Omitted when the filters are default. */
	f?: string;
	/** Column KEY being sorted (not the server sort field). */
	sort?: string;
	dir?: "asc" | "desc";
	page?: number;
	size?: number;
};

/** Encode filter state for the URL. Returns undefined when there's nothing to store. */
export function encodeFilters(
	filters: ColumnFiltersState,
): string | undefined {
	if (!filters || filters.length === 0) return undefined;
	try {
		return JSON.stringify(filters);
	} catch {
		// A filter value that can't be serialized (cyclic/undefined) should
		// degrade to "no filters in the URL", never crash the navigation.
		return undefined;
	}
}

/**
 * Decode filter state from the URL. Returns null (not an empty array) when the
 * param is absent or unparseable, so callers can tell "no param, use the
 * default filters" apart from "an explicitly empty filter set".
 */
export function decodeFilters(raw: unknown): ColumnFiltersState | null {
	if (typeof raw !== "string" || raw === "") return null;
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) return null;
		// Shape-check each entry — the URL is user-editable, so a malformed
		// blob must not reach the table as a filter object.
		const valid = parsed.filter(
			(e): e is { id: string; value: unknown } =>
				typeof e === "object" &&
				e !== null &&
				typeof (e as { id?: unknown }).id === "string",
		);
		return valid;
	} catch {
		return null;
	}
}

/** Parse/clamp a 1-based page number from an untrusted search param. */
export function decodePage(raw: unknown): number | null {
	const n = typeof raw === "number" ? raw : Number(raw);
	if (!Number.isFinite(n) || n < 1) return null;
	return Math.floor(n);
}

/** TanStack Router `validateSearch` for routes hosting the voter-file browser. */
export function validateVoterFileSearch(
	search: Record<string, unknown>,
): VoterFileSearch {
	const out: VoterFileSearch = {};
	if (typeof search.f === "string" && search.f !== "") out.f = search.f;
	if (typeof search.sort === "string" && search.sort !== "")
		out.sort = search.sort;
	if (search.dir === "asc" || search.dir === "desc") out.dir = search.dir;
	const page = decodePage(search.page);
	if (page !== null && page > 1) out.page = page;
	const size = decodePage(search.size);
	if (size !== null) out.size = size;
	return out;
}

/**
 * Contacts adds a free-text search box that /your-voters doesn't have (the
 * voter-file page treats the Name column filter as its search), so it carries
 * one extra param on top of the shared shape.
 */
export type ContactsSearch = VoterFileSearch & { q?: string };

/** TanStack Router `validateSearch` for the contacts list route. */
export function validateContactsSearch(
	search: Record<string, unknown>,
): ContactsSearch {
	const out: ContactsSearch = validateVoterFileSearch(search);
	if (typeof search.q === "string" && search.q !== "") out.q = search.q;
	return out;
}

/**
 * Lists a detail page can be opened from, and therefore safely returned to.
 *
 * Set only by a list row click, so its presence on a detail route is what
 * proves the previous history entry is that list. `useCanGoBack()` alone only
 * reports that history exists — it would happily send a user who arrived from
 * the dashboard or an external link back to somewhere unrelated.
 */
export type ListOrigin = "/your-voters" | "/contacts";
